import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import AddCommentRoundedIcon from "@mui/icons-material/AddCommentRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { useLocation, useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { LoginUserAtom } from "../stores/LoginUserAtom";
import ConstList from "../utils/ConstList";
import { designTokens } from "../theme";

type Project = {
  project_id: number; company_id: number; company_name: string; project_name: string;
  project_summary?: string; project_start_reason?: string; customer_type?: "prospective" | "existing";
  customer_type_id?: number;
  status: "active" | "closed" | "deleted"; next_action?: string;
  closed_reason?: string;
  next_schedule?: string; progress_status?: string;
};
type Report = {
  sales_record_id: number; sales_date?: string; sales_person?: string;
  contact_person?: string; proposal_details?: string; customer_needs?: string;
  request_inquiry?: string; issue_complaint?: string;
  next_action?: string; next_action_details?: string; next_response_details?: string;
  next_schedule?: string;
};
type Props = { projectId: number; onBack: () => void };
const display = (value?: string) => value || "—";

export default function ProjectDetail({ projectId, onBack }: Props) {
  const user = useRecoilValue(LoginUserAtom);
  const navigateTo = useNavigate();
  const location = useLocation();
  const navigate = (to: string, options?: { state?: { returnTo?: string } }) => {
    if (to.includes("view=detail")) {
      const returnTo = options?.state?.returnTo ?? `${location.pathname}${location.search}`;
      const separator = returnTo.includes("?") ? "&" : "?";
      return navigateTo(to, { ...options, state: { ...options?.state, returnTo: `${returnTo}${separator}projectId=${projectId}` } });
    }
    return navigateTo(to, options);
  };
  const [project, setProject] = useState<Project | null>(null);
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editSummary, setEditSummary] = useState("");
  const [editStartReason, setEditStartReason] = useState("");
  const [editClosedReason, setEditClosedReason] = useState("");
  const [editCustomerType, setEditCustomerType] = useState<1 | 2>(1);
  const [saving, setSaving] = useState(false);

  const request = useCallback((payload: Record<string, unknown>) => axios.post(
    ConstList.getApiUrl() + "ProjectMaster.php",
    { ...payload, access_token: user.accessToken, algorithm: user.algorithm },
    ConstList.AXIOS_CONFIG,
  ), [user.accessToken, user.algorithm]);

  const fetchDetail = useCallback(async () => {
    if (!user.accessToken) return;
    setLoading(true);
    try {
      const [detailResponse, reportResponse] = await Promise.all([
        request({ flag: "Project_Detail", project_id: projectId }),
        request({ flag: "Project_Report_List", project_id: projectId }),
      ]);
      if (detailResponse.data?.data === false) throw new Error(detailResponse.data.message);
      if (reportResponse.data?.data === false) throw new Error(reportResponse.data.message);
      setProject(detailResponse.data?.data?.project ?? null);
      const reportList = reportResponse.data?.data?.reports ?? [];
      setReports([...reportList].sort((a: Report, b: Report) =>
        String(a.sales_date ?? "").localeCompare(String(b.sales_date ?? "")) ||
        Number(a.sales_record_id) - Number(b.sales_record_id)
      ));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "案件情報の取得に失敗しました。");
    } finally { setLoading(false); }
  }, [projectId, request, user.accessToken]);

  useEffect(() => { void fetchDetail(); }, [fetchDetail]);

  const openEdit = () => {
    if (!project || project.status === "deleted") return;
    setEditName(project.project_name ?? "");
    setEditSummary(project.project_summary ?? "");
    setEditStartReason(project.project_start_reason ?? "");
    setEditClosedReason(project.closed_reason ?? "");
    setEditCustomerType(Number(project.customer_type_id) === 2 ? 2 : 1);
    setEditOpen(true);
  };

  const saveProject = async () => {
    if (!project || !editName.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const response = await request({
        flag: "Project_Update",
        project_id: project.project_id,
        project_name: editName.trim(),
        project_summary: editSummary.trim(),
        project_start_reason: editStartReason.trim(),
        closed_reason: editClosedReason.trim(),
        customer_type_id: editCustomerType,
      });
      if (response.data?.data === false || response.data?.status === 0) {
        throw new Error(response.data?.message ?? "案件の更新に失敗しました");
      }
      setEditOpen(false);
      await fetchDetail();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "案件の更新に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const goToCreate = () => {
    if (!project || project.status === "deleted") return;
    setConfirmOpen(false);
    const customerType =
      project.customer_type ??
      (Number(project.customer_type_id) === 2 ? "existing" : "prospective");
    const returnTo = `${location.pathname}${location.search}${location.search ? "&" : "?"}projectId=${project.project_id}`;
    navigate(`/sales-reports?view=create&projectId=${project.project_id}&companyId=${project.company_id}&customerType=${customerType}`, { state: { returnTo } });
  };

  if (loading) return <Box p={4} textAlign="center"><CircularProgress /></Box>;
  if (!project) return <Alert severity="error">{error ?? "案件が見つかりません。"}</Alert>;

  const resolvedCustomerType =
    project.customer_type ??
    (Number(project.customer_type_id) === 2 ? "existing" : "prospective");

  const summaryFields = [
    ["会社名", project.company_name], ["案件名", project.project_name],
    ["案件概要", project.project_summary], ["案件開始理由", project.project_start_reason],
    ["顧客区分", resolvedCustomerType === "prospective" ? "見込み顧客" : "既存顧客"],
    ["進捗状況", project.progress_status],
    ["案件状態", project.status === "active" ? "進行中" : project.status === "closed" ? "終了" : "論理削除済み"],
    ["次回アクション", project.next_action], ["次回予定", project.next_schedule],
  ];

  return <Paper sx={{ p: { xs: 2, md: 4 }, backgroundColor: designTokens.surface.paper, border: `1px solid ${designTokens.border.default}`, borderTop: `6px solid ${designTokens.brand.primary}`, boxShadow: "0 10px 28px rgba(15,23,42,0.08)", "& .MuiPaper-outlined": { borderColor: designTokens.brand.primary, borderLeft: `5px solid ${designTokens.brand.primary}`, backgroundColor: "#FFFFFF", boxShadow: "0 3px 10px rgba(0,149,175,0.08)" } }}>
    <Stack spacing={2.5}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
        <Box><Typography variant="h5" fontWeight={900}>案件詳細</Typography><Typography color="text.secondary">{project.company_name} ／ {project.project_name}</Typography></Box>
        <Stack direction="row" spacing={1}><Button startIcon={<ArrowBackRoundedIcon />} onClick={onBack}>案件一覧へ戻る</Button><Button variant="contained" startIcon={<AddCommentRoundedIcon />} disabled={project.status === "deleted"} onClick={() => setConfirmOpen(true)}>この案件の日報を作成</Button></Stack>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="outlined" startIcon={<EditRoundedIcon />} disabled={project.status === "deleted"} onClick={openEdit}>案件を編集</Button>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        {summaryFields.map(([label, value], index) => <Box key={label} sx={{ p: 1.5, borderBottom: `1px solid ${designTokens.border.default}`, gridColumn: index === 2 ? "1 / -1" : undefined }}><Typography variant="caption" color="text.secondary" fontWeight={800}>{label}</Typography><Typography sx={{ whiteSpace: "pre-wrap" }}>{display(value)}</Typography></Box>)}
      </Box>
      <Divider />
      {project.status === "closed" && (
        <Alert severity="info">
          案件終了理由：{display(project.closed_reason)}
        </Alert>
      )}
      <Box><Typography variant="h6" fontWeight={900} mb={1}>紐づく日報（{reports.length}件）</Typography>{reports.length === 0 ? <Typography color="text.secondary">この案件に紐づく日報はありません。</Typography> : <Stack spacing={1}>{reports.map(report => <Paper key={report.sales_record_id} variant="outlined" onClick={() => navigate(`/sales-reports?view=detail&id=${report.sales_record_id}&customer_type=${resolvedCustomerType}`, { state: { returnTo: `${location.pathname}${location.search}` } })} sx={{ p: 1.5, cursor: "pointer", "&:hover": { borderColor: designTokens.brand.primary, backgroundColor: designTokens.surface.subtle } }}><Typography fontWeight={800}>{report.sales_date || "日付未入力"} ／ {display(report.sales_person)}</Typography><Typography variant="body2">顧客窓口：{display(report.contact_person)}</Typography><Typography variant="body2">提案内容：{display(report.proposal_details)}</Typography><Typography variant="body2">課題・ニーズ：{display(report.customer_needs)}</Typography><Typography variant="body2">次回：{display(report.next_action || report.next_action_details || report.next_response_details)}</Typography></Paper>)}</Stack>}</Box>
    </Stack>
    <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} fullWidth maxWidth="sm"><DialogTitle>この案件の日報を作成します</DialogTitle><DialogContent><Typography>会社名：{project.company_name}</Typography><Typography>案件名：{project.project_name}</Typography><Typography>顧客区分：{resolvedCustomerType === "prospective" ? "見込み顧客" : "既存顧客"}</Typography><Typography mt={2}>この顧客区分の日報フォームで作成しますか？</Typography>{project.status === "closed" && <Alert severity="warning" sx={{ mt: 2 }}>終了済み案件です。過去対応の記録として作成しますか？</Alert>}</DialogContent><DialogActions><Button onClick={() => setConfirmOpen(false)}>キャンセル</Button><Button variant="contained" onClick={goToCreate}>日報作成へ</Button></DialogActions></Dialog>
    <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>案件を編集</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography color="text.secondary">会社名：{project.company_name}</Typography>
          <TextField label="案件名" required fullWidth value={editName} onChange={event => setEditName(event.target.value)} />
          <TextField label="案件概要" fullWidth multiline minRows={3} value={editSummary} onChange={event => setEditSummary(event.target.value)} />
          <TextField label="案件開始理由" fullWidth multiline minRows={2} value={editStartReason} onChange={event => setEditStartReason(event.target.value)} />
          <TextField label="案件終了理由" fullWidth multiline minRows={2} value={editClosedReason} onChange={event => setEditClosedReason(event.target.value)} helperText="終了済み案件の場合に入力してください。" />
          <TextField select label="顧客区分" fullWidth value={editCustomerType} onChange={event => setEditCustomerType(Number(event.target.value) === 2 ? 2 : 1)}>
            <MenuItem value={1}>見込み顧客</MenuItem>
            <MenuItem value={2}>既存顧客</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEditOpen(false)}>キャンセル</Button>
        <Button variant="contained" disabled={!editName.trim() || saving} onClick={() => void saveProject()}>{saving ? "保存中..." : "保存"}</Button>
      </DialogActions>
    </Dialog>
  </Paper>;
}
